import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToAdminDashboard } from "../router/dashboardNavigation";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Route as RouteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
  ReceiptLong as ReceiptLongIcon,
  Category as CategoryIcon,
  FolderOpen as FolderOpenIcon,
  LocalShipping as LocalShippingOutlinedIcon,
  PeopleAlt as PeopleAltOutlinedIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

import {
  useGetTripsQuery,
  useAddTripMutation,
  useUpdateTripMutation,
  useStartTripMutation,
  useCompleteTripMutation,
  useCancelTripMutation,
  useDeleteTripMutation,
  useGetMaterialsQuery,
  useAddMaterialMutation,
  useUpdateMaterialMutation,
  useDeleteMaterialMutation,
  useGetDocumentsQuery,
  useAddDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useAddExpenseMutation,
  useGetClientsQuery,
  useAddClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} from "../redux/logistics/logisticsApi";

import AddTripModal from "./modals/AddTripModal";
import EditTripModal from "./modals/EditTripModal";
import AddMaterialModal from "./modals/AddMaterialModal";
import EditMaterialModal from "./modals/EditMaterialModal";
import AddDocumentModal from "./modals/AddDocumentModal";
import AddExpenseModal from "./modals/AddExpenseModal";
import AddClientModal from "./modals/AddClientModal";
import EditClientModal from "./modals/EditClientModal";
import { ROLES } from "../utils/rbacHelper";
import { useGetAllUsersQuery } from "../redux/admin/dashboardApi";
import { useGetAllLiteDevicesQuery } from "../redux/admin/adminApi";
import ScrollableTableContainer from "./common/ScrollableTableContainer";
import FlexibleStatsRow from "./common/FlexibleStatsRow";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

// ✅ Format dates properly
function formatDate(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-IN', options);
  } catch (e) {
    return value;
  }
}

function unwrapMongo(value) {
  // Handles: { $oid: "..." } and { $date: "..." }
  if (value && typeof value === "object") {
    if ("$oid" in value) return value.$oid;
    if ("$date" in value) return value.$date;
  }
  return value;
}

function toReadableLabel(key = "") {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatLocationLike(value) {
  if (!value || typeof value !== "object") return null;
  const parts = [];
  if (value.address) parts.push(String(value.address));
  if (value.area) parts.push(String(value.area));
  if (value.city) parts.push(String(value.city));
  if (value.state) parts.push(String(value.state));
  if (value.pincode) parts.push(String(value.pincode));
  if (value.country) parts.push(String(value.country));
  if (value.lat != null && value.lng != null) parts.push(`(${value.lat}, ${value.lng})`);
  return parts.length ? parts.join(", ") : null;
}

function formatEntityLike(value, lookups = {}) {
  if (!value || typeof value !== "object") return null;

  const resolvedId =
    (value._id && resolveLookupLabel(value._id, lookups)) ||
    (value.id && resolveLookupLabel(value.id, lookups));

  const primary =
    value.username ||
    value.name ||
    value.routeName ||
    value.title ||
    value.materialName ||
    value.expenseType ||
    value.sensorName ||
    value.deviceName ||
    value.clientName ||
    value.fileName ||
    value.originalName ||
    value.documentName ||
    value.type ||
    resolvedId;

  const secondary =
    value.email ||
    value.mobileNo ||
    value.phone ||
    value.deviceId ||
    value.imei ||
    value.uniqueId ||
    value.category;

  if (!primary && !secondary) return null;
  return [primary, secondary].filter(Boolean).join(" • ");
}

function isMongoIdLike(value) {
  return typeof value === "string" && /^[a-f0-9]{24}$/i.test(value.trim());
}

function resolveLookupLabel(value, lookups = {}) {
  const id = unwrapMongo(value);
  if (!id || !isMongoIdLike(String(id))) return null;

  return (
    lookups.usersById?.get(String(id)) ||
    lookups.materialsById?.get(String(id)) ||
    lookups.clientsById?.get(String(id)) ||
    lookups.devicesById?.get(String(id)) ||
    null
  );
}

function summarizePlainObject(value, lookups = {}) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const entitySummary = formatEntityLike(value, lookups);
  if (entitySummary) return entitySummary;

  const locationSummary = formatLocationLike(value);
  if (locationSummary) return locationSummary;

  const entries = Object.entries(value)
    .map(([k, v]) => [k, unwrapMongo(v)])
    .filter(([, v]) => v !== null && v !== undefined && v !== "" && typeof v !== "object")
    .slice(0, 4)
    .map(([k, v]) => `${toReadableLabel(k)}: ${resolveLookupLabel(v, lookups) || v}`);

  return entries.length ? entries.join(" • ") : "Details available";
}

function formatValue(value, key, lookups = {}) {
  if (value === null || value === undefined) return "—";

  // ✅ unwrap Mongo wrappers early
  value = unwrapMongo(value);

  // ✅ Treat source/destination as location objects
  const k = String(key || "").toLowerCase();
  const lookupLabel = resolveLookupLabel(value, lookups);
  if (lookupLabel) return lookupLabel;

  if (k === "source" || k === "destination") {
    if (typeof value === "object" && value) return formatLocationLike(value) || "—";
    return String(value);
  }

  // ✅ Handle ObjectId-like fields: createdBy, assignedToDevice, etc
  if (k.includes("by") || k.includes("assignedto")) {
    // unwrap again in case nested { $oid }
    const v = unwrapMongo(value);

    if (typeof v === "object" && v) {
      // if populated user
      if (v.username || v.name || v.email) return String(v.username || v.name || v.email);

      // if objectid stored in _id
      if (v._id) return resolveLookupLabel(v._id, lookups) || String(unwrapMongo(v._id));
      return "—";
    }
    return resolveLookupLabel(v, lookups) || String(v);
  }

  // ✅ Dates
  if (k.includes("date") || k.includes("time") || k.includes("at")) {
    const v = unwrapMongo(value);
    return formatDate(v);
  }

  // ✅ calibration / calliberation: array of objects
  if (k === "calibration" || k === "calliberation" || k.includes("calibr")) {
    if (Array.isArray(value)) {
      if (!value.length) return "—";
      // show first few points nicely
      return value
        .slice(0, 3)
        .map(p => `v:${p.value ?? "—"} volt:${p.voltage ?? "—"}`)
        .join(" | ") + (value.length > 3 ? ` (+${value.length - 3} more)` : "");
    }
    if (typeof value === "object" && value) {
      const rows = Array.isArray(value?.rows) ? value.rows : Array.isArray(value?.points) ? value.points : null;
      if (rows?.length) {
        return rows
          .slice(0, 3)
          .map((p) => `v:${p.value ?? "—"} volt:${p.voltage ?? "—"}`)
          .join(" | ") + (rows.length > 3 ? ` (+${rows.length - 3} more)` : "");
      }
    }
    if (typeof value === "object" && value) return summarizePlainObject(value, lookups);
    return String(value);
  }

  // ✅ Generic location/address objects even if key isn't "address"
  if (typeof value === "object" && value && (value.address || value.lat != null || value.lng != null)) {
    return formatLocationLike(value) || "—";
  }

  // ✅ Arrays
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    if (typeof value[0] === "object" && value[0] !== null) {
      return value
        .slice(0, 3)
        .map((item) => summarizePlainObject(item, lookups))
        .filter(Boolean)
        .join(" | ") + (value.length > 3 ? ` (+${value.length - 3} more)` : "");
    }
    return value.map((item) => resolveLookupLabel(item, lookups) || item).join(", ");
  }

  // ✅ Booleans
  if (typeof value === "boolean") return value ? "Yes" : "No";

  // ✅ Objects fallback: NEVER return raw object/json dump
  if (typeof value === "object") return summarizePlainObject(value, lookups);

  return String(value);
}

function getStatusChipMeta(formattedValue, key) {
  const text = String(formattedValue || "").trim();
  const normalized = text.toLowerCase();

  if (
    ["status", "tripStatus", "paymentStatus", "approvalStatus", "state"].includes(key) ||
    key.toLowerCase().includes("status")
  ) {
    if (["completed", "active", "approved", "paid", "online", "valid"].includes(normalized)) {
      return { label: text, color: "success" };
    }
    if (["pending", "planned", "scheduled", "draft", "in_progress", "started"].includes(normalized)) {
      return { label: text.replaceAll("_", " "), color: "warning" };
    }
    if (["cancelled", "rejected", "expired", "offline", "failed"].includes(normalized)) {
      return { label: text, color: "error" };
    }
    return { label: text.replaceAll("_", " "), color: "default" };
  }

  if (key === "isActive" || key === "active") {
    return { label: normalized === "yes" ? "Active" : "Inactive", color: normalized === "yes" ? "success" : "default" };
  }

  return null;
}

function normalizeCollectionResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.expenses)) return payload.expenses;
  if (Array.isArray(payload.materials)) return payload.materials;
  if (Array.isArray(payload.licenses)) return payload.licenses;
  if (Array.isArray(payload.documents)) return payload.documents;
  if (Array.isArray(payload.routes)) return payload.routes;
  if (Array.isArray(payload.sensors)) return payload.sensors;
  if (Array.isArray(payload.reminders)) return payload.reminders;
  if (Array.isArray(payload.trips)) return payload.trips;
  if (Array.isArray(payload.clients)) return payload.clients;
  if (Array.isArray(payload.users)) return payload.users;
  return [];
}


// ✅ Get dynamic columns from data
function getDynamicColumns(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  const allKeys = new Set();
  const excludeKeys = ["_id", "id", "__v", "password", "token"];
  
  data.forEach(item => {
    Object.keys(item).forEach(key => {
      if (!excludeKeys.includes(key)) {
        allKeys.add(key);
      }
    });
  });
  
  // Sort: createdAt/updatedAt at end, rest alphabetically
  const columns = Array.from(allKeys).sort((a, b) => {
    const aIsDate = a.toLowerCase().includes("createdat") || a.toLowerCase().includes("updatedat");
    const bIsDate = b.toLowerCase().includes("createdat") || b.toLowerCase().includes("updatedat");
    
    if (aIsDate && !bIsDate) return 1;
    if (!aIsDate && bIsDate) return -1;
    
    return a.localeCompare(b);
  });
  
  return columns.map(key => ({
    key,
    label: key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }));
}

function getRowId(row) {
  return row?._id || row?.id || Math.random();
}

function resolveTripLifecycle(row) {
  const tripStatus = String(row?.tripStatus || "").trim().toLowerCase();
  if (tripStatus) return tripStatus;

  const legacy = String(row?.status || "").trim().toLowerCase();
  if (legacy === "scheduled") return "planned";
  if (legacy === "active") return "in_progress";
  if (legacy === "completed") return "completed";
  if (legacy === "cancelled") return "cancelled";
  return "planned";
}




const TABS = [
  { key: "trips", label: "Trips" },
  { key: "clients", label: "Clients" },
  { key: "materials", label: "Materials" },
  { key: "documents", label: "Documents" },
];

const PAGE_META = {
  trips: {
    title: "Trips",
    subtitle: "Manage trip routes, distance, duration, drivers and client deliveries",
    addLabel: "Add Trip",
    searchPlaceholder: "Search trips...",
    cardIcon: <LocalShippingOutlinedIcon fontSize="small" />,
  },
  clients: {
    title: "Clients",
    subtitle: "Manage client details, contacts, VAT and map location",
    addLabel: "Add Client",
    searchPlaceholder: "Search clients...",
    cardIcon: <PeopleAltOutlinedIcon fontSize="small" />,
  },
  materials: {
    title: "Materials",
    subtitle: "Manage material categories, rates and active status",
    addLabel: "Add Material",
    searchPlaceholder: "Search materials...",
    cardIcon: <CategoryIcon fontSize="small" />,
  },
  documents: {
    title: "Documents",
    subtitle: "Manage logistic documents and uploaded files",
    addLabel: "Add Document",
    searchPlaceholder: "Search documents...",
    cardIcon: <FolderOpenIcon fontSize="small" />,
  },
};

const CHART_COLORS = ["#2563eb", "#22c55e", "#f97316", "#a855f7", "#ef4444", "#06b6d4"];

const parseNumericValue = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const match = String(value).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : 0;
};

const summarizeDateKey = (value, formatOptions = { day: "2-digit", month: "short" }) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-GB", formatOptions).format(date);
};

const extractCity = (row) => {
  const raw =
    row?.clientAddress ||
    row?.address ||
    row?.city ||
    row?.location?.city ||
    row?.location?.address ||
    "";
  if (!raw) return "Unknown";
  const parts = String(raw).split(",").map((part) => part.trim()).filter(Boolean);
  if (row?.city) return String(row.city);
  return parts.length > 1 ? parts[parts.length - 2] || parts[parts.length - 1] : parts[0];
};

export default function LogisticsDashboard({ forcedTabKey = null, hideTabs = false, embedded = false }) {
  const navigate = useNavigate();
  const currentUserRole = String(useSelector((state) => state?.auth?.user?.role || "")).toLowerCase();
  const canManageLogistics = [
    ROLES.SUPER_ADMIN,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.USER,
    ROLES.CLIENT,
  ].includes(currentUserRole);
  const [activeTab, setActiveTab] = useState(() => {
    const defaultIndex = TABS.findIndex((tab) => tab.key === forcedTabKey);
    return defaultIndex >= 0 ? defaultIndex : 0;
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tripActionModal, setTripActionModal] = useState({ type: null, row: null });
  const [shareDialog, setShareDialog] = useState({ open: false, item: null, mode: "email", links: null });
  const [shareEmail, setShareEmail] = useState("");
  const [expandedCells, setExpandedCells] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tablePage, setTablePage] = useState(1);

  const currentTabKey = forcedTabKey || TABS[activeTab].key;
  const currentMeta = PAGE_META[currentTabKey] || PAGE_META.trips;

  // ✅ QUERIES
  const tripsQ = useGetTripsQuery(undefined, { skip: currentTabKey !== "trips" });
  const materialsQ = useGetMaterialsQuery(undefined, { skip: currentTabKey !== "materials" });
  const documentsQ = useGetDocumentsQuery(undefined, { skip: currentTabKey !== "documents" });
  const clientsQ = useGetClientsQuery(undefined, { skip: currentTabKey !== "clients" });
  const allUsersQ = useGetAllUsersQuery();
  const allDevicesQ = useGetAllLiteDevicesQuery();
  const materialsLookupQ = useGetMaterialsQuery();

  // ✅ MUTATIONS
  const [addTrip] = useAddTripMutation();
  const [updateTrip] = useUpdateTripMutation();
  const [startTrip] = useStartTripMutation();
  const [completeTrip] = useCompleteTripMutation();
  const [cancelTrip] = useCancelTripMutation();
  const [deleteTrip] = useDeleteTripMutation();
  const [addMaterial] = useAddMaterialMutation();
  const [updateMaterial] = useUpdateMaterialMutation();
  const [deleteMaterial] = useDeleteMaterialMutation();
  const [addDocument] = useAddDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [addExpense] = useAddExpenseMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [addClient] = useAddClientMutation();
  const [updateClient] = useUpdateClientMutation();
  const [deleteClient] = useDeleteClientMutation();

  // ✅ HANDLERS
  const handleAdd = () => setShowAddModal(true);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleShare = (item) => {
    const getRouteCoords = (row) => {
      const src = row?.source || row?.startPoint || {};
      const dst = row?.destination || row?.endPoint || {};
      return {
        sLat: Number(src?.lat),
        sLng: Number(src?.lng),
        eLat: Number(dst?.lat),
        eLng: Number(dst?.lng),
      };
    };

    if (currentTabKey === "trips" || currentTabKey === "routes") {
      const { sLat, sLng, eLat, eLng } = getRouteCoords(item);
      if ([sLat, sLng, eLat, eLng].every(Number.isFinite)) {
        const origin = `${sLat},${sLng}`;
        const destination = `${eLat},${eLng}`;
        const routeLink = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
        setShareDialog({
          open: true,
          item,
          mode: "map",
          links: {
            routeLink,
            startLink: `https://www.google.com/maps?q=${origin}`,
            endLink: `https://www.google.com/maps?q=${destination}`,
          },
        });
        return;
      }
    }
    setShareDialog({ open: true, item, mode: "email", links: null });
    setShareEmail("");
  };

  const handleShowRouteInMap = (routeRow) => {
    const src = routeRow?.source || {};
    const dst = routeRow?.destination || {};
    const startPoint = routeRow?.startPoint || {};
    const endPoint = routeRow?.endPoint || {};
    const payload = {
      _id: routeRow?._id || routeRow?.id,
      routeName: routeRow?.routeName || routeRow?.name || "Route",
      source: {
        lat: Number(src?.lat ?? startPoint?.lat),
        lng: Number(src?.lng ?? startPoint?.lng),
        address: src?.address || routeRow?.startAddr || "",
      },
      destination: {
        lat: Number(dst?.lat ?? endPoint?.lat),
        lng: Number(dst?.lng ?? endPoint?.lng),
        address: dst?.address || routeRow?.endAddr || "",
      },
    };

    if (
      ![payload.source.lat, payload.source.lng, payload.destination.lat, payload.destination.lng].every(
        Number.isFinite,
      )
    ) {
      alert("Route source/destination coordinates are missing.");
      return;
    }

    navigateToAdminDashboard(navigate, {
      tab: "map",
      focusRoute: payload,
    });
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      alert("Link copied");
    } catch {
      window.prompt("Copy this link:", link);
    }
  };

  const shareLink = async (link, title = "Trip Route") => {
    try {
      if (navigator.share) {
        await navigator.share({ title, text: link, url: link });
      } else {
        await copyLink(link);
      }
    } catch {
      // user cancelled
    }
  };

  const handleShareSubmit = () => {
    if (!shareEmail) {
      alert("Please enter an email address");
      return;
    }
    
    // Add your share API call here
    console.log("Sharing with:", shareEmail, "Item:", shareDialog.item);
    alert(`Shared successfully with ${shareEmail}!`);
    setShareDialog({ open: false, item: null });
  };

  const toggleCellExpansion = (rowId, colKey) => {
    const cellId = `${rowId}-${colKey}`;
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellId)) {
        newSet.delete(cellId);
      } else {
        newSet.add(cellId);
      }
      return newSet;
    });
  };

  const isCellExpanded = (rowId, colKey) => {
    return expandedCells.has(`${rowId}-${colKey}`);
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const mutations = {
        trips: deleteTrip,
        clients: deleteClient,
        materials: deleteMaterial,
        documents: deleteDocument,
      };
      
      await mutations[currentTabKey]?.(id).unwrap();
      alert("Deleted successfully!");
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Failed to delete: " + (err?.data?.error || err.message));
    }
  };

  const handleTripTransition = async (row, action) => {
    const id = getRowId(row);
    try {
      if (action === "start") {
        await startTrip(id).unwrap();
      } else if (action === "complete") {
        await completeTrip({ id }).unwrap();
      } else if (action === "cancel") {
        await cancelTrip({ id }).unwrap();
      }
    } catch (err) {
      alert(err?.data?.error || err?.data?.msg || "Trip action failed");
    }
  };

  const handleSubmitAdd = async (formData) => {
    try {
      const payload =
        currentTabKey === "documents"
          ? (() => {
              const fd = new FormData();
              Object.keys(formData || {}).forEach((key) => {
                if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
                  fd.append(key, formData[key]);
                }
              });
              return fd;
            })()
          : formData;

      const mutations = {
        trips: addTrip,
        clients: addClient,
        materials: addMaterial,
        documents: addDocument,
      };
      
      await mutations[currentTabKey]?.(payload).unwrap();
      alert("Added successfully!");
      setShowAddModal(false);
    } catch (err) {
      console.error("Add Error:", err);
      alert("Failed to add: " + (err?.data?.error || err.message));
    }
  };

  const handleSubmitEdit = async (id, formData) => {
    try {
      if (currentTabKey === "documents") {
        const fd = new FormData();
        Object.keys(formData || {}).forEach((key) => {
          if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
            fd.append(key, formData[key]);
          }
        });
        await updateDocument({ id, body: fd }).unwrap();
      } else {
        const mutations = {
          trips: updateTrip,
          clients: updateClient,
          materials: updateMaterial,
        };

        await mutations[currentTabKey]?.({ id, ...formData }).unwrap();
      }
      alert("Updated successfully!");
      setShowEditModal(false);
      setSelectedItem(null);
    } catch (err) {
      console.error("Update Error:", err);
      alert("Failed to update: " + (err?.data?.error || err.message));
    }
  };

  const openTripActionModal = (type, row) => {
    setTripActionModal({ type, row });
  };

  const closeTripActionModal = () => {
    setTripActionModal({ type: null, row: null });
  };

  const handleTripDocumentSubmit = async (formData) => {
    try {
      const fd = new FormData();
      Object.entries(formData || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });
      const tripLabel =
        tripActionModal?.row?.routeName ||
        tripActionModal?.row?.client?.clientName ||
        tripActionModal?.row?._id ||
        "Trip";
      if (!fd.get("description")) {
        fd.append("description", `Trip document - ${tripLabel}`);
      }
      await addDocument(fd).unwrap();
      alert("Document added successfully!");
      closeTripActionModal();
    } catch (err) {
      console.error("Trip Document Add Error:", err);
      alert("Failed to add document: " + (err?.data?.error || err.message));
    }
  };

  const handleTripExpenseSubmit = async (formData) => {
    try {
      const fd = new FormData();
      Object.entries(formData || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });
      const response = await addExpense(fd).unwrap();
      alert(
        response?.isDemoLocalOnly
          ? "Demo mode: Expense was saved only in this browser so you can experience the workflow. Live billing data was not changed."
          : "Expense added successfully!"
      );
      closeTripActionModal();
    } catch (err) {
      console.error("Trip Expense Add Error:", err);
      alert("Failed to add expense: " + (err?.data?.error || err?.data?.msg || err.message));
    }
  };

  // ✅ Get Current Data
  const getCurrentData = () => {
    const queryMap = {
      trips: tripsQ.data,
      clients: clientsQ.data,
      materials: materialsQ.data,
      documents: documentsQ.data,
    };
    
    const data = queryMap[currentTabKey];
    return normalizeCollectionResponse(data);
  };

  const getCurrentQuery = () => {
    const queries = {
      trips: tripsQ,
      clients: clientsQ,
      materials: materialsQ,
      documents: documentsQ,
    };
    return queries[currentTabKey] || { isLoading: false, isError: false };
  };

  const rows = getCurrentData();
  const query = getCurrentQuery();
  const columns = getDynamicColumns(rows);
  const queryErrorMessage =
    query?.error?.data?.error ||
    query?.error?.data?.msg ||
    query?.error?.error ||
    "Failed to load data";
  const lookupData = useMemo(() => {
    const users = normalizeCollectionResponse(allUsersQ.data);
    const devices = normalizeCollectionResponse(allDevicesQ.data);
    const materials = normalizeCollectionResponse(materialsLookupQ.data);
    const clients = normalizeCollectionResponse(clientsQ.data);

    return {
      usersById: new Map(
        users.map((user) => [
          String(user?._id || user?.id),
          user?.username || user?.name || user?.email || user?.mobileNo || String(user?._id || user?.id),
        ]),
      ),
      devicesById: new Map(
        devices.map((device) => [
          String(device?._id || device?.id),
          device?.name || device?.deviceName || device?.imei || device?.deviceId || String(device?._id || device?.id),
        ]),
      ),
      materialsById: new Map(
        materials.map((material) => [
          String(material?._id || material?.id),
          material?.name || material?.materialName || material?.category || String(material?._id || material?.id),
        ]),
      ),
      clientsById: new Map(
        clients.map((client) => [
          String(client?._id || client?.id),
          client?.clientName || client?.name || client?.contactPersonName || String(client?._id || client?.id),
        ]),
      ),
    };
  }, [allUsersQ.data, allDevicesQ.data, materialsLookupQ.data, clientsQ.data]);
  const filteredRows = useMemo(() => {
    const queryText = searchQuery.trim().toLowerCase();
    if (!queryText) return rows;

    return rows.filter((row) =>
      columns.some((col) =>
        String(formatValue(row?.[col.key], col.key, lookupData) || "")
          .toLowerCase()
          .includes(queryText)
      )
    );
  }, [columns, lookupData, rows, searchQuery]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (tablePage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, rowsPerPage, tablePage]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const handleDownloadCurrentData = () => {
    if (!filteredRows?.length) {
      alert("No data available to download.");
      return;
    }
    const headers = columns.map((c) => c.label);
    const csvRows = filteredRows.map((row) =>
      columns
        .map((col) => {
          const value = String(formatValue(row?.[col.key], col.key, lookupData) || "");
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentTabKey}-data-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const dashboardSummary = useMemo(() => {
    if (currentTabKey === "trips") {
      const completed = rows.filter((row) => resolveTripLifecycle(row) === "completed").length;
      const pending = rows.filter((row) => ["planned", "in_progress"].includes(resolveTripLifecycle(row))).length;
      const totalDistance = rows.reduce(
        (sum, row) =>
          sum +
          parseNumericValue(
            row?.distance || row?.routeLength || row?.distanceKm || row?.tripDistance
          ),
        0
      );
      return [
        { label: "Total Trips", value: rows.length, helper: "All time", tone: "#2563eb" },
        { label: "Completed Trips", value: completed, helper: `${rows.length ? Math.round((completed / rows.length) * 100) : 0}% of total`, tone: "#16a34a" },
        { label: "Pending Trips", value: pending, helper: `${rows.length ? Math.round((pending / rows.length) * 100) : 0}% of total`, tone: "#f97316" },
        { label: "Total Distance", value: `${totalDistance.toFixed(2)} km`, helper: "All time", tone: "#7c3aed" },
      ];
    }

    if (currentTabKey === "clients") {
      const active = rows.filter((row) => row?.isActive === true || String(row?.active || "").toLowerCase() === "yes").length;
      const individual = rows.filter((row) => String(row?.entityType || "").toLowerCase().includes("individual")).length;
      const business = rows.filter((row) => !String(row?.entityType || "").toLowerCase().includes("individual")).length;
      return [
        { label: "Total Clients", value: rows.length, helper: "All time", tone: "#2563eb" },
        { label: "Active Clients", value: active, helper: `${rows.length ? Math.round((active / rows.length) * 100) : 0}% of total`, tone: "#16a34a" },
        { label: "Individual Clients", value: individual, helper: `${rows.length ? Math.round((individual / rows.length) * 100) : 0}% of total`, tone: "#7c3aed" },
        { label: "Business Clients", value: business, helper: `${rows.length ? Math.round((business / rows.length) * 100) : 0}% of total`, tone: "#f97316" },
      ];
    }

    if (currentTabKey === "materials") {
      const active = rows.filter((row) => row?.isActive === true || String(row?.active || "").toLowerCase() === "yes").length;
      const categories = new Set(rows.map((row) => String(row?.category || "").trim()).filter(Boolean)).size;
      const avgRate = rows.length ? rows.reduce((sum, row) => sum + parseNumericValue(row?.rate), 0) / rows.length : 0;
      return [
        { label: "Total Materials", value: rows.length, helper: "All time", tone: "#2563eb" },
        { label: "Active Materials", value: active, helper: `${rows.length ? Math.round((active / rows.length) * 100) : 0}% of total`, tone: "#16a34a" },
        { label: "Categories", value: categories, helper: "All time", tone: "#7c3aed" },
        { label: "Average Rate", value: `₹ ${avgRate.toFixed(2)}`, helper: "Per unit", tone: "#f59e0b" },
      ];
    }

    const uploadedFiles = rows.filter((row) => row?.file || row?.document || row?.fileUrl || row?.url).length;
    const activeDocs = rows.filter((row) => row?.isActive === true || String(row?.status || "").toLowerCase() === "active").length;
    const recentUploads = rows.filter((row) => {
      const date = new Date(row?.createdAt || row?.uploadedAt || row?.updatedAt);
      if (Number.isNaN(date.getTime())) return false;
      return Date.now() - date.getTime() <= 7 * 24 * 60 * 60 * 1000;
    }).length;
    return [
      { label: "Total Documents", value: rows.length, helper: "All time", tone: "#2563eb" },
      { label: "Uploaded Files", value: uploadedFiles, helper: rows.length ? `${Math.round((uploadedFiles / rows.length) * 100)}% of total` : "0% of total", tone: "#16a34a" },
      { label: "Active Documents", value: activeDocs, helper: rows.length ? `${Math.round((activeDocs / rows.length) * 100)}% of total` : "0% of total", tone: "#7c3aed" },
      { label: "Recent Uploads", value: recentUploads, helper: "Last 7 days", tone: "#f97316" },
    ];
  }, [currentTabKey, rows]);

  const dashboardSummaryCards = useMemo(
    () =>
      dashboardSummary.map((card, index) => {
        const iconByLabel = (() => {
          const text = String(card.label || "").toLowerCase();
          if (text.includes("trip")) return <RouteIcon fontSize="small" />;
          if (text.includes("client")) return <PeopleAltOutlinedIcon fontSize="small" />;
          if (text.includes("material") || text.includes("categor")) return <CategoryIcon fontSize="small" />;
          if (text.includes("document") || text.includes("upload")) return <FolderOpenIcon fontSize="small" />;
          if (text.includes("distance")) return <LocalShippingOutlinedIcon fontSize="small" />;
          if (text.includes("rate")) return <ReceiptLongIcon fontSize="small" />;
          return <DescriptionIcon fontSize="small" />;
        })();

        const toneByColor = (() => {
          switch (card.tone) {
            case "#16a34a":
              return "success";
            case "#f97316":
            case "#f59e0b":
              return "warning";
            case "#7c3aed":
              return "violet";
            case "#ef4444":
            case "#dc2626":
              return "danger";
            default:
              return index === dashboardSummary.length - 1 ? "info" : "neutral";
          }
        })();

        return {
          key: `${currentTabKey}-${card.label}-${index}`,
          label: card.label,
          value: card.value,
          detail: card.helper,
          tone: toneByColor,
          icon: iconByLabel,
        };
      }),
    [currentTabKey, dashboardSummary]
  );

  const primaryChartData = useMemo(() => {
    if (currentTabKey === "trips") {
      const buckets = new Map();
      rows.forEach((row) => {
        const key = summarizeDateKey(row?.startDate || row?.createdAt);
        const current = buckets.get(key) || { label: key, trips: 0, distance: 0 };
        current.trips += 1;
        current.distance += parseNumericValue(row?.distance || row?.routeLength || row?.distanceKm);
        buckets.set(key, current);
      });
      return Array.from(buckets.values()).slice(-8);
    }
    if (currentTabKey === "clients") {
      const buckets = new Map();
      rows.forEach((row) => {
        const key = summarizeDateKey(row?.createdAt, { month: "short" });
        const current = buckets.get(key) || { label: key, count: 0 };
        current.count += 1;
        buckets.set(key, current);
      });
      return Array.from(buckets.values()).slice(-12);
    }
    if (currentTabKey === "materials") {
      const buckets = new Map();
      rows.forEach((row) => {
        const key = String(row?.category || "Others");
        const current = buckets.get(key) || { label: key, value: 0 };
        current.value += 1;
        buckets.set(key, current);
      });
      return Array.from(buckets.values());
    }
    const buckets = new Map();
    rows.forEach((row) => {
      const key = String(row?.type || "Others");
      const current = buckets.get(key) || { label: key, value: 0 };
      current.value += 1;
      buckets.set(key, current);
    });
    return Array.from(buckets.values());
  }, [currentTabKey, rows]);

  const secondaryChartData = useMemo(() => {
    if (currentTabKey === "trips") {
      const statusCounts = [
        { name: "Completed", value: rows.filter((row) => resolveTripLifecycle(row) === "completed").length },
        { name: "Pending", value: rows.filter((row) => ["planned", "in_progress"].includes(resolveTripLifecycle(row))).length },
        { name: "Cancelled", value: rows.filter((row) => resolveTripLifecycle(row) === "cancelled").length },
      ];
      return statusCounts.filter((item) => item.value > 0);
    }
    if (currentTabKey === "clients") {
      const typeCounts = [
        { name: "Individual", value: rows.filter((row) => String(row?.entityType || "").toLowerCase().includes("individual")).length },
        { name: "Business", value: rows.filter((row) => !String(row?.entityType || "").toLowerCase().includes("individual")).length },
      ];
      return typeCounts.filter((item) => item.value > 0);
    }
    if (currentTabKey === "materials") {
      return rows.map((row, index) => ({
        label: row?.name || `Material ${index + 1}`,
        value: parseNumericValue(row?.rate),
      })).slice(0, 8);
    }
    const buckets = new Map();
    rows.forEach((row) => {
      const key = summarizeDateKey(row?.createdAt || row?.uploadedAt);
      const current = buckets.get(key) || { label: key, value: 0 };
      current.value += 1;
      buckets.set(key, current);
    });
    return Array.from(buckets.values()).slice(-12);
  }, [currentTabKey, rows]);

  const tertiaryChartData = useMemo(() => {
    if (currentTabKey !== "clients") return [];
    const cityCounts = new Map();
    rows.forEach((row) => {
      const city = extractCity(row);
      cityCounts.set(city, (cityCounts.get(city) || 0) + 1);
    });
    return Array.from(cityCounts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [currentTabKey, rows]);

  // ✅ Render Modals
  const renderAddModal = () => {
    if (!showAddModal) return null;

    const modals = {
      trips: <AddTripModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      clients: <AddClientModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      materials: <AddMaterialModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
      documents: <AddDocumentModal onClose={() => setShowAddModal(false)} onSubmit={handleSubmitAdd} />,
    };
    
    return modals[currentTabKey];
  };

  const renderEditModal = () => {
    if (!showEditModal || !selectedItem) return null;

    const modals = {
      trips: <EditTripModal key={`edit-trip-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} tripData={selectedItem} />,
      clients: <EditClientModal key={`edit-client-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} clientData={selectedItem} />,
      materials: <EditMaterialModal key={`edit-material-${getRowId(selectedItem)}`} onClose={() => setShowEditModal(false)} onSubmit={handleSubmitEdit} materialData={selectedItem} />,
      documents: (
        <AddDocumentModal
          key={`edit-document-${getRowId(selectedItem)}`}
          onClose={() => setShowEditModal(false)}
          onSubmit={(formData) => handleSubmitEdit(getRowId(selectedItem), formData)}
          initialData={selectedItem}
          title="Edit Document"
          submitLabel="UPDATE"
        />
      ),
    };
    
    return modals[currentTabKey];
  };

  const renderTripActionModal = () => {
    if (!tripActionModal?.type) return null;

    const row = tripActionModal.row || {};
    const tripVehicleId = row?.vehicle?._id || row?.vehicle || "";

    if (tripActionModal.type === "document") {
      return (
        <AddDocumentModal
          key={`trip-document-${getRowId(row)}`}
          onClose={closeTripActionModal}
          onSubmit={handleTripDocumentSubmit}
          initialData={{
            name: row?.routeName || "",
            type: "Trip",
            description: row?.startAddr && row?.endAddr ? `${row.startAddr} → ${row.endAddr}` : "",
          }}
        />
      );
    }

    if (tripActionModal.type === "expense") {
      return (
        <AddExpenseModal
          key={`trip-expense-${getRowId(row)}`}
          onClose={closeTripActionModal}
          onSubmit={handleTripExpenseSubmit}
          initialData={{
            vehicle: tripVehicleId,
            date: new Date().toISOString().slice(0, 10),
            description: row?.routeName ? `Trip expense - ${row.routeName}` : "",
          }}
        />
      );
    }

    return null;
  };

  const renderSummaryCards = () => (
    <FlexibleStatsRow items={dashboardSummaryCards} />
  );

  const renderCharts = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          currentTabKey === "clients"
            ? { xs: "1fr", lg: "1.15fr 0.95fr 0.7fr" }
            : { xs: "1fr", lg: "1.1fr 0.9fr" },
        gap: 1.35,
      }}
    >
      <Paper sx={{ borderRadius: 3, border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 16px 38px rgba(15,23,42,0.05)", p: { xs: 1.8, lg: 1.85 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
            {currentTabKey === "trips"
              ? "Trip Count Trend"
              : currentTabKey === "clients"
              ? "Client Growth Trend"
              : currentTabKey === "materials"
              ? "Materials by Category"
              : "Documents by Type"}
          </Typography>
        </Stack>
        <Box sx={{ width: "100%", height: { xs: 240, lg: 208 } }}>
          {currentTabKey === "materials" || currentTabKey === "documents" ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={primaryChartData} dataKey="value" nameKey="label" innerRadius={58} outerRadius={90} paddingAngle={3}>
                  {primaryChartData.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer>
              <LineChart data={primaryChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <RechartsTooltip />
                <Line
                  type="monotone"
                  dataKey={currentTabKey === "trips" ? "trips" : "count"}
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3, border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 16px 38px rgba(15,23,42,0.05)", p: { xs: 1.8, lg: 1.85 } }}>
        <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
          {currentTabKey === "trips"
            ? "Completion Status"
            : currentTabKey === "clients"
            ? "Entity Type Distribution"
            : currentTabKey === "materials"
            ? "Rate Distribution"
            : "Upload Trend"}
        </Typography>
        <Box sx={{ width: "100%", height: { xs: 240, lg: 208 } }}>
          {currentTabKey === "trips" || currentTabKey === "clients" ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={secondaryChartData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={88} paddingAngle={2}>
                  {secondaryChartData.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer>
              <BarChart data={secondaryChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} hide={currentTabKey === "materials"} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <RechartsTooltip />
                <Bar dataKey="value" fill={currentTabKey === "materials" ? "#22c55e" : "#2563eb"} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      {currentTabKey === "clients" ? (
        <Paper sx={{ borderRadius: 3, border: "1px solid rgba(226,232,240,0.95)", boxShadow: "0 16px 38px rgba(15,23,42,0.05)", p: { xs: 1.8, lg: 1.85 } }}>
          <Typography sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>Top Cities</Typography>
          <Stack spacing={1.25}>
            {tertiaryChartData.length ? (
              tertiaryChartData.map((item, index) => (
                <Box key={`${item.label}-${index}`}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                    <Typography sx={{ fontSize: 13.5, color: "#0f172a", fontWeight: 700 }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>{item.value}</Typography>
                  </Stack>
                  <Box sx={{ height: 7, borderRadius: 999, bgcolor: "#e2e8f0", overflow: "hidden" }}>
                    <Box sx={{ width: `${Math.max(8, (item.value / Math.max(tertiaryChartData[0]?.value || 1, 1)) * 100)}%`, height: "100%", bgcolor: "#2563eb" }} />
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No city data available.</Typography>
            )}
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );

  return (
    <>
      <Box sx={{ p: embedded ? 0 : 2.5, bgcolor: embedded ? "transparent" : "#f8fafc", minHeight: embedded ? "auto" : "100vh" }}>
        {!hideTabs && !forcedTabKey ? (
          <Paper sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                setSearchQuery("");
                setTablePage(1);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 1.5, pt: 0.5 }}
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  sx={{
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 15,
                    minHeight: 46,
                  }}
                />
              ))}
            </Tabs>
          </Paper>
        ) : null}

        {forcedTabKey ? (
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent={embedded ? "flex-end" : "space-between"}
            alignItems={{ xs: embedded ? "stretch" : "flex-start", md: "center" }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            {!embedded ? (
              <Box>
                <Typography sx={{ fontSize: { xs: "2rem", md: "2.25rem" }, fontWeight: 900, color: "#0f172a" }}>
                  {currentMeta.title}
                </Typography>
                <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15 }}>
                  {currentMeta.subtitle}
                </Typography>
              </Box>
            ) : null}
            {canManageLogistics ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  alignSelf: { xs: "stretch", md: "auto" },
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 800,
                  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                  boxShadow: "0 16px 32px rgba(37,99,235,0.22)",
                }}
              >
                {currentMeta.addLabel}
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {forcedTabKey ? renderSummaryCards() : null}
        {forcedTabKey ? <Box sx={{ mt: 1.8 }}>{renderCharts()}</Box> : null}

        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {query.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {queryErrorMessage}
          </Alert>
        )}

        {!query.isLoading && !query.isError && (
          <Paper sx={{ mt: forcedTabKey ? 1.8 : 0, borderRadius: forcedTabKey ? 3 : 2, overflow: "hidden", border: forcedTabKey ? "1px solid rgba(226,232,240,0.95)" : "none", boxShadow: forcedTabKey ? "0 16px 38px rgba(15,23,42,0.05)" : "none" }}>
            <Box sx={{ p: 1.8, borderBottom: "1px solid #e5e7eb" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 1.4 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                  {currentMeta.title} ({filteredRows.length})
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 96 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setTablePage(1);
                      }}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <MenuItem key={size} value={size}>{size} per page</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton onClick={() => query?.refetch?.()} sx={{ color: "#6b7280", border: "1px solid #e2e8f0", borderRadius: 2 }}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download / Export">
                    <span>
                      <IconButton onClick={handleDownloadCurrentData} sx={{ color: "#6b7280", border: "1px solid #e2e8f0", borderRadius: 2 }} disabled={!filteredRows.length}>
                        <DownloadIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: 1.5, alignItems: "center" }}>
                <TextField
                  placeholder={currentMeta.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setTablePage(1);
                  }}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      borderRadius: 2.5,
                    },
                  }}
                  InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {!forcedTabKey && canManageLogistics ? (
                    <IconButton onClick={handleAdd} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              </Box>
            </Box>

            <ScrollableTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.key} sx={{ fontWeight: 'bold', bgcolor: "#f8fafc", whiteSpace: "nowrap" }}>
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', width: 200, bgcolor: "#f8fafc" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No data found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => {
                      const id = getRowId(row);

                      return (
                        <TableRow key={id} hover>
                          {columns.map((col) => {
                            const value = row?.[col.key];
                            const formatted = formatValue(value, col.key, lookupData);
                            const statusMeta = getStatusChipMeta(formatted, col.key);
                            const isExpanded = isCellExpanded(id, col.key);
                            const shouldTruncate = formatted && formatted.length > 50;
                            
                            // Ensure formatted is always a string
                            const displayText = String(formatted || "—");

                            return (
                              <TableCell 
                                key={col.key}
                                onClick={() => shouldTruncate && toggleCellExpansion(id, col.key)}
                                sx={{ 
                                  cursor: shouldTruncate ? 'pointer' : 'default',
                                  maxWidth: 300,
                                  whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: isExpanded ? 'clip' : 'ellipsis'
                                }}
                              >
                                {statusMeta ? (
                                  <Chip
                                    size="small"
                                    label={statusMeta.label}
                                    color={statusMeta.color}
                                    variant={statusMeta.color === "default" ? "outlined" : "filled"}
                                    sx={{ textTransform: "capitalize" }}
                                  />
                                ) : (
                                  <Stack spacing={0.35}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                        fontWeight: col.key.toLowerCase().includes("name") ? 600 : 400,
                                      }}
                                    >
                                      {isExpanded ? displayText : (shouldTruncate ? truncateText(displayText) : displayText)}
                                    </Typography>
                                    {!isExpanded && shouldTruncate && (
                                      <Typography variant="caption" color="primary">
                                        Click to view more
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                            );
                          })}

                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {canManageLogistics && (
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEdit(row)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {currentTabKey === "trips" && (
                                <>
                                  <Tooltip title="Add Document">
                                    <IconButton size="small" color="primary" onClick={() => openTripActionModal("document", row)}>
                                      <DescriptionIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Add Expense">
                                    <IconButton size="small" color="primary" onClick={() => openTripActionModal("expense", row)}>
                                      <ReceiptLongIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Open Route">
                                    <IconButton size="small" color="primary" onClick={() => handleShowRouteInMap(row)}>
                                      <RouteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {canManageLogistics && (
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>

            <Box sx={{ px: 2, py: 1.75, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {filteredRows.length ? (tablePage - 1) * rowsPerPage + 1 : 0} to {Math.min(tablePage * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
              </Typography>
              <Pagination page={tablePage} count={totalPages} onChange={(_, value) => setTablePage(value)} shape="rounded" />
            </Box>
          </Paper>
        )}

      {renderAddModal()}
      {renderEditModal()}
      {renderTripActionModal()}

        {/* Share Dialog */}
        <MuiDialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, item: null, mode: "email", links: null })}>
          <MuiDialogTitle>Share {TABS[activeTab].label.slice(0, -1)}</MuiDialogTitle>
          <MuiDialogContent>
            {shareDialog.mode === "map" && shareDialog.links ? (
              <Box sx={{ mt: 1, minWidth: 360, display: "grid", gap: 1 }}>
                <Button
                  startIcon={<RouteIcon />}
                  variant="outlined"
                  onClick={() => shareLink(shareDialog.links.routeLink, "Trip Route")}
                >
                  Share Route Link
                </Button>
                <Button
                  startIcon={<ContentCopyIcon />}
                  variant="outlined"
                  onClick={() => copyLink(shareDialog.links.routeLink)}
                >
                  Copy Route Link
                </Button>
                <Button
                  variant="text"
                  onClick={() => copyLink(shareDialog.links.startLink)}
                >
                  Copy Start Point
                </Button>
                <Button
                  variant="text"
                  onClick={() => copyLink(shareDialog.links.endLink)}
                >
                  Copy End Point
                </Button>
              </Box>
            ) : (
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="user@example.com"
                sx={{ mt: 2 }}
              />
            )}
          </MuiDialogContent>
          <MuiDialogActions>
            <Button onClick={() => setShareDialog({ open: false, item: null, mode: "email", links: null })}>
              Cancel
            </Button>
            {shareDialog.mode !== "map" && (
              <Button onClick={handleShareSubmit} variant="contained">
                Share
              </Button>
            )}
          </MuiDialogActions>
        </MuiDialog>
      </Box>
    </>
  );
}
